import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CONTEUDO_CHAVES, getConteudoPublic } from '@/api/adminApi'
import { getClientLoginPath } from '@/config/tenant'
import { useTenant } from '@/contexts/TenantContext'
import { subscribeConteudoUpdated } from '@/utils/conteudoSync'
import { consumeAuthNotice } from '@/utils/authFlow'
import { clearGuestSession, markGuestSessionActive } from '@/utils/guestAuth'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import styles from './TipoAcessoPage.module.css'

function IconCliente({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="8" r="3.3" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5.5 20c.8-3.2 3.3-5 6.5-5s5.7 1.8 6.5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

function IconProfissional({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="8" r="3.3" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5.5 20c.8-3.2 3.3-5 6.5-5s5.7 1.8 6.5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M18 6l1.2 1.2L21 5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function IconAdmin({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M9 12l2 2 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function IconGuest({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const ACCESS_OPTIONS = [
  {
    id: 'cliente',
    label: 'Cliente',
    hint: 'Já tem conta? Entre com e-mail e código.',
    Icon: IconCliente,
  },
  {
    id: 'profissional',
    label: 'Profissional',
    hint: 'Acesso da equipa de atendimento.',
    Icon: IconProfissional,
  },
  {
    id: 'admin',
    label: 'Admin',
    hint: 'Gestão do estabelecimento.',
    Icon: IconAdmin,
  },
  {
    id: 'guest',
    label: 'Guest',
    hint: 'Explorar e marcar — contacto só ao confirmar.',
    Icon: IconGuest,
  },
] as const

export function TipoAcessoPage() {
  const navigate = useNavigate()
  const { nome: empresaNome, logoUrl: tenantLogoUrl, slug: tenantSlug } = useTenant()
  const [logoUrl, setLogoUrl] = useState<string | null>(tenantLogoUrl)
  const [authNotice, setAuthNotice] = useState<string | null>(null)

  useEffect(() => {
    clearGuestSession()
    setAuthNotice(consumeAuthNotice('cliente'))
  }, [])

  const loadLogo = useCallback(() => {
    return getConteudoPublic()
      .then((data) => {
        const logo = data[CONTEUDO_CHAVES.app_logo_url] ?? null
        setLogoUrl(logo ?? tenantLogoUrl)
      })
      .catch(() => {
        setLogoUrl(tenantLogoUrl)
      })
  }, [tenantLogoUrl])

  useEffect(() => {
    let cancelled = false
    const run = () => {
      if (cancelled) return
      void loadLogo()
    }
    run()
    const unsub = subscribeConteudoUpdated(run)
    return () => {
      cancelled = true
      unsub()
    }
  }, [tenantSlug, loadLogo])

  const handleSelect = (id: (typeof ACCESS_OPTIONS)[number]['id']) => {
    if (id === 'cliente') {
      navigate(getClientLoginPath())
      return
    }
    if (id === 'profissional') {
      navigate('/profissional')
      return
    }
    if (id === 'admin') {
      navigate('/admin')
      return
    }
    markGuestSessionActive()
    navigate('/menu', { replace: true })
  }

  const displayLogo = resolveMediaUrl(logoUrl || tenantLogoUrl || '')

  return (
    <main className={`standalone-page ${styles.page} animate-slide-up`}>
      <div className={styles.logoBlock}>
        <div className={styles.logo}>
          {displayLogo ? (
            <img src={displayLogo} alt="Logo" className={styles.logoImg} />
          ) : (
            'LOGO'
          )}
        </div>
        <span className={styles.appName}>{empresaNome}</span>
      </div>

      <div className={styles.card}>
        <span className={styles.kicker}>Bem-vindo</span>
        <h1 className={styles.heading}>Como deseja entrar?</h1>
        <p className={styles.subheading}>
          Escolha o tipo de acesso. Como visitante, pode explorar a app e indicar e-mail e telefone apenas ao
          confirmar uma marcação.
        </p>
        {authNotice && (
          <p className={styles.authNotice} role="alert">
            {authNotice}
          </p>
        )}

        <div className={styles.options}>
          {ACCESS_OPTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.optionBtn}
              onClick={() => handleSelect(item.id)}
            >
              <span className={styles.optionIcon}>
                <item.Icon className={styles.optionIconSvg} />
              </span>
              <span>{item.label}</span>
              <span className={styles.optionHint}>{item.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </main>
  )
}
